import React, { useEffect, useRef, useState, useContext } from 'react';
import * as Cesium from 'cesium';
import 'cesium/Build/Cesium/Widgets/widgets.css';
import axios from 'axios';
import { db } from '../firebase';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import UserContext from '../context/UserContext';

// Configure Cesium for development environment
window.CESIUM_BASE_URL = '/cesium/';
Cesium.buildModuleUrl.setBaseUrl('/cesium/');

// Set Cesium Ion access token
Cesium.Ion.defaultAccessToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiI4Mzk3NDRkOC1jM2YwLTQ2ZDgtODNhMy0yNDE1N2RkNDJlZGQiLCJpZCI6MzQwODk5LCJpYXQiOjE3NTc3MjAxMjF9.TeWYZ8SQ_t-QaO8Si48fFwPqOk5IhBtvP4syuEg9ef0';

// Class component to avoid React Strict Mode issues with Cesium
class CesiumViewer extends React.Component {
  constructor(props) {
    super(props);
    this.viewerRef = React.createRef();
    this.viewer = null;
    this.interval = null;
    this.issEntity = null;
    this.userZoomLevel = 4000000; // Default zoom level (meters above ISS)
    this.animationSetup = false; // Prevent duplicate animation setup
    this.useFallbackAPI = false; // Flag to use fallback API permanently
  }

  componentDidMount() {
    // Prevent duplicate mounting due to React Strict Mode
    if (!this._mounted) {
      this._mounted = true;
      // Clear any stale cache on first mount
      this.clearCache();
      this.initializeViewer();
    }
  }



  componentWillUnmount() {
    this._mounted = false;
    if (this.interval) {
      clearInterval(this.interval);
    }
    if (this.viewer && !this.viewer.isDestroyed()) {
      this.viewer.destroy();
    }
  }

  clearCache = () => {
    console.log('🧹 Clearing position cache for fresh data fetch');
    this.lastTLEFetch = null;
  };

  initializeViewer = async () => {
    try {
      // Prevent duplicate initialization
      if (this.viewer && !this.viewer.isDestroyed()) {
        console.log('Viewer already initialized, skipping...');
        return;
      }

      // Clean up any existing viewer
      if (this.viewer && this.viewer.isDestroyed()) {
        this.viewer = null;
      }

      this.viewer = new Cesium.Viewer(this.viewerRef.current, {
        terrainProvider: new Cesium.EllipsoidTerrainProvider(),
        baseLayerPicker: false,
        geocoder: false,
        homeButton: false,
        sceneModePicker: false,
        timeline: false,
        animation: false,
        navigationHelpButton: false,
        fullscreenButton: false,
        infoBox: true,
        selectionIndicator: true,
        skyBox: false,
        skyAtmosphere: false,
        sun: true,
        moon: true,
      });

      // Disable all user input controls - we'll use a zoom slider instead
      this.viewer.scene.screenSpaceCameraController.enableInputs = false;
      this.viewer.scene.screenSpaceCameraController.enableTranslate = false;
      this.viewer.scene.screenSpaceCameraController.enableZoom = false;
      this.viewer.scene.screenSpaceCameraController.enableRotate = false;
      this.viewer.scene.screenSpaceCameraController.enableTilt = false;

      // Get initial ISS position to set camera
      const initialTLE = await this.fetchISSTLE();
      const initialPosition = this.calculateISSPosition(initialTLE);

      if (initialPosition) {
        // Set camera to view ISS from directly above
        // Position camera at ISS coordinates but much higher altitude
        this.viewer.camera.setView({
          destination: Cesium.Cartesian3.fromDegrees(
            initialPosition.longitude,
            initialPosition.latitude,
            4000000 // 4 million meters above Earth
          ),
          orientation: {
            heading: 0.0,
            pitch: Cesium.Math.toRadians(-90.0), // Look straight down
            roll: 0.0
          }
        });

        // Camera positioned successfully
      }

      // Set up smooth orbital animation
      await this.setupSmoothAnimation();

      // Notify parent component
      this.props.onViewerReady();

    } catch (error) {
      console.error('❌ Error creating Cesium viewer:', error);
      this.props.onViewerError('Failed to initialize 3D globe');
    }
  };



  fetchISSTLE = async () => {
    // Use fallback API if we've already determined Open Notify is failing
    if (this.useFallbackAPI) {
      return this.fetchFromFallbackAPI();
    }

    // First try Open Notify API
    const openNotifyUrl = 'https://api.open-notify.org/iss-now.json';
    try {
      const response = await axios.get(openNotifyUrl, {
        timeout: 3000, // Reduced timeout
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });

      if (response.data && response.data.iss_position) {
        const lat = parseFloat(response.data.iss_position.latitude);
        const lon = parseFloat(response.data.iss_position.longitude);
        this.currentPosition = {
          latitude: lat,
          longitude: lon,
          altitude: 408000, // Approximate ISS altitude in meters
          timestamp: new Date(response.data.timestamp * 1000)
        };
        this.lastTLEFetch = new Date();
        if (this.props.onDataSourceChange) {
          this.props.onDataSourceChange(true);
        }
        return ['DIRECT_POSITION', 'FROM_OPEN_NOTIFY'];
      } else {
        console.warn('⚠️ Open Notify returned invalid response format');
        // Switch to fallback permanently
        this.useFallbackAPI = true;
        return this.fetchFromFallbackAPI();
      }
    } catch (error) {
      console.log('🔄 Open Notify failed, switching to fallback API permanently');
      // Switch to fallback API permanently for this session
      this.useFallbackAPI = true;
      return this.fetchFromFallbackAPI();
    }
  };

  fetchFromFallbackAPI = async () => {
    // Use Where The ISS At API as fallback
    try {
      const altResponse = await axios.get('https://api.wheretheiss.at/v1/satellites/25544', {
        timeout: 5000,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });

      if (altResponse.data && altResponse.data.latitude && altResponse.data.longitude) {
        this.currentPosition = {
          latitude: parseFloat(altResponse.data.latitude),
          longitude: parseFloat(altResponse.data.longitude),
          altitude: 408000, // Approximate ISS altitude in meters
          timestamp: new Date(altResponse.data.timestamp * 1000)
        };
        this.lastTLEFetch = new Date();
        if (this.props.onDataSourceChange) {
          this.props.onDataSourceChange(true);
        }
        return ['DIRECT_POSITION', 'FROM_WHERETHEISSAT'];
      }
    } catch (altError) {
      console.error('❌ Fallback ISS API failed:', altError.message);
    }
    return null;
  };

  calculateISSPosition = (tle) => {
    try {
      // Return direct position data from API
      if (tle && tle[0] === 'DIRECT_POSITION' && this.currentPosition) {
        return this.currentPosition;
      }
    } catch (error) {
      console.warn('Error calculating ISS position:', error);
    }
    return null;
  };

  setupSmoothAnimation = async () => {
    try {
      // Prevent duplicate animation setup
      if (this.animationSetup) {
        console.log('Animation already set up, skipping...');
        return;
      }

      // Ensure viewer is fully initialized and ready
      if (!this.viewer || this.viewer.isDestroyed()) {
        console.warn('Viewer not initialized');
        return;
      }

      if (!this.viewer.entities) {
        console.warn('Viewer entities not available yet');
        return;
      }

      const tle = await this.fetchISSTLE();
      if (!tle) return;

      // Mark animation as set up
      this.animationSetup = true;

      console.log('🚀 Setting up ISS position tracking...');

      // Create ISS entity with initial position
      const initialPosition = this.calculateISSPosition(tle);
      if (initialPosition && !this.issEntity) {
        const initialCartesian = Cesium.Cartesian3.fromDegrees(
          initialPosition.longitude,
          initialPosition.latitude,
          initialPosition.altitude
        );

        this.issEntity = this.viewer.entities.add({
          id: 'iss',
          name: 'International Space Station',
          position: initialCartesian,
          point: {
            pixelSize: 20,
            color: Cesium.Color.YELLOW,
            outlineColor: Cesium.Color.BLACK,
            outlineWidth: 4,
          },
          label: {
            text: '🛰️ ISS',
            font: '18pt sans-serif',
            fillColor: Cesium.Color.YELLOW,
            outlineColor: Cesium.Color.BLACK,
            outlineWidth: 4,
            style: Cesium.LabelStyle.FILL_AND_OUTLINE,
            verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
            pixelOffset: new Cesium.Cartesian2(0, -30),
          }
        });
      }

      // Set up position updates and camera following
      this.interval = setInterval(async () => {
        if (this.viewer && !this.viewer.isDestroyed() && this.issEntity) {
          try {
            // Fetch fresh data every 2 seconds
            const tle = await this.fetchISSTLE();
            const currentPosition = this.calculateISSPosition(tle);

            if (currentPosition) {
              // Update ISS entity position
              const newCartesianPosition = Cesium.Cartesian3.fromDegrees(
                currentPosition.longitude,
                currentPosition.latitude,
                currentPosition.altitude
              );

              this.issEntity.position = newCartesianPosition;

              // Keep camera centered above ISS coordinates with current zoom level
              const cameraHeight = Math.max(100000, Math.min(10000000, this.userZoomLevel));

              // Set camera to look down at ISS coordinates from above
              this.viewer.camera.setView({
                destination: Cesium.Cartesian3.fromDegrees(
                  currentPosition.longitude,
                  currentPosition.latitude,
                  cameraHeight
                ),
                orientation: {
                  heading: 0.0,
                  pitch: Cesium.Math.toRadians(-90.0), // Straight down
                  roll: 0.0
                }
              });

              // Update parent component with current ISS data
              this.props.onISSData(currentPosition);
            }
          } catch (error) {
            console.warn('Error updating ISS position:', error);
          }
        }
      }, 2000); // Update every 2 seconds for Open Notify API

      this.props.onLoadingComplete();
      console.log('✅ ISS position tracking set up successfully');

    } catch (error) {
      console.error('❌ Error setting up ISS tracking:', error);
      this.props.onError('Failed to set up ISS tracking');
    }
  };

  handleZoomChange = (newZoomLevel) => {
    this.userZoomLevel = newZoomLevel;

    // Immediately update camera position using cached ISS position
    if (this.viewer && !this.viewer.isDestroyed() && this.currentPosition) {
      try {
        // Position camera directly above current ISS coordinates at new zoom level
        // No need to fetch new data - use cached position
        this.viewer.camera.setView({
          destination: Cesium.Cartesian3.fromDegrees(
            this.currentPosition.longitude,
            this.currentPosition.latitude,
            this.userZoomLevel
          ),
          orientation: {
            heading: 0.0,
            pitch: Cesium.Math.toRadians(-90.0), // Straight down
            roll: 0.0
          }
        });
      } catch (error) {
        console.warn('Error updating zoom:', error);
      }
    }
  };

  render() {
    return <div ref={this.viewerRef} className="w-full h-full" />;
  }
}

const ISSVisualizer = () => {
  const [issData, setIssData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewerReady, setViewerReady] = useState(false);
  const [usingDirectPosition, setUsingDirectPosition] = useState(false);
  const cesiumViewerRef = useRef(null);

  const [issLivestreams, setIssLivestreams] = useState([]);
  const [livestreamsLoading, setLivestreamsLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [theatreLayout, setTheatreLayout] = useState('vertical'); // 'vertical' or 'horizontal'
  const { user } = useContext(UserContext);



  // Handle zoom changes from the slider
  const handleZoomChange = (newZoomLevel) => {
    if (cesiumViewerRef.current && cesiumViewerRef.current.handleZoomChange) {
      cesiumViewerRef.current.handleZoomChange(newZoomLevel);
    }
  };



  // Fetch ISS-related livestreams from Firestore
  const fetchISSLivestreams = async () => {
    if (!user) {
      setLivestreamsLoading(false);
      return;
    }

    try {
      setLivestreamsLoading(true);

      // Fetch livestreams from Firestore
      const livestreamsRef = collection(db, 'Livestreams');
      const q = query(livestreamsRef, orderBy('timestamp', 'desc'));
      const querySnapshot = await getDocs(q);

      const allStreams = [];
      querySnapshot.forEach((doc) => {
        allStreams.push({ id: doc.id, ...doc.data() });
      });

      // Filter for ISS-related streams
      // Include streams that contain "ISS" or "Space Station" in title
      // But exclude streams that only contain "mission" without ISS context
      const issStreams = allStreams.filter(stream => {
        const title = stream.title ? stream.title.toLowerCase() : '';

        // Check for ISS-related terms with word boundaries to avoid false matches
        const hasISS = /\biss\b/.test(title) ||
                      title.includes('space station') ||
                      title.includes('international space station');

        // Exclude if it only has "mission" and no ISS terms
        const hasOnlyMission = title.includes('mission') && !hasISS;

        const shouldInclude = hasISS && !hasOnlyMission;

        // No debug logging needed

        return shouldInclude;
      });

      // Take up to 6 streams for display
      const selectedStreams = issStreams.slice(0, 6).map(stream => ({
        name: stream.title,
        url: `https://www.youtube.com/embed/${stream.id}`,
        description: stream.channelTitle || 'ISS Live Stream'
      }));

      setIssLivestreams(selectedStreams);
      setLivestreamsLoading(false);
    } catch (error) {
      console.error('Error fetching ISS livestreams:', error);
      setLivestreamsLoading(false);
    }
  };



  // Fetch livestreams when user is available
  useEffect(() => {
    if (user) {
      fetchISSLivestreams();
    }
  }, [user]);

  return (
    <div className="w-full min-h-screen bg-gray-900">
      {/* Globe Section - Conditionally rendered based on theatre layout */}
      {selectedVideo === null || theatreLayout === 'vertical' ? (
        <div className={`relative ${selectedVideo !== null ? 'h-[50vh]' : 'h-[40vh]'} w-full`}>
          <CesiumViewer
            ref={cesiumViewerRef}
            onViewerReady={() => setViewerReady(true)}
            onViewerError={(error) => setError(error)}
            onISSData={(data) => setIssData(data)}
            onLoadingComplete={() => setLoading(false)}
            onError={(error) => setError(error)}
            onDataSourceChange={(usingDirect) => setUsingDirectPosition(usingDirect)}
          />

          {/* Control Panel */}
          <div className="absolute top-4 left-4 bg-black bg-opacity-75 text-white p-4 rounded-lg w-64">
            <h2 className="text-xl font-bold mb-3">🛰️ ISS Live Tracker</h2>

            {loading ? (
              <div className="space-y-2">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                <p className="text-sm text-gray-300">Loading ISS data...</p>
              </div>
            ) : error ? (
              <div className="space-y-2">
                <p className="text-sm text-red-400">⚠️ {error}</p>
                <p className="text-xs text-gray-400">Using fallback data</p>
              </div>
            ) : issData ? (
              <div className="space-y-2">
                <div className="text-sm">
                  <p><strong>Latitude:</strong> {issData.latitude.toFixed(4)}°</p>
                  <p><strong>Longitude:</strong> {issData.longitude.toFixed(4)}°</p>
                  <p><strong>Altitude:</strong> {(issData.altitude / 1000).toFixed(1)} km</p>
                </div>
                <p className="text-xs text-gray-400">
                  Last updated: {issData.timestamp.toLocaleTimeString()}
                </p>
              </div>
            ) : null}

            {/* Controls */}
            <div className="mt-4 space-y-3">
              {/* Zoom Slider */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-300 flex items-center">
                   Zoom Level
                </label>
                <input
                  type="range"
                  min="100000"
                  max="10000000"
                  step="100000"
                  defaultValue="4000000"
                  onChange={(e) => handleZoomChange(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-xs text-gray-400">
                  <span>Close</span>
                  <span>Far</span>
                </div>
              </div>
            </div>

            {/* Legend */}
            <div className="mt-4 space-y-2">
              <h3 className="text-sm font-semibold text-gray-300">Legend</h3>
              <div className="space-y-1 text-xs">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                  <span className="text-gray-300">🛰️ ISS Position</span>
                </div>
              </div>
            </div>
          </div>

          {/* Loading Overlay */}
          {loading && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
              <div className="bg-black bg-opacity-90 text-white p-6 rounded-lg text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <h3 className="text-lg font-semibold mb-2">Loading ISS Tracker...</h3>
                <p className="text-sm text-gray-300">Initializing 3D globe and fetching ISS data</p>
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* Livestreams Section */}
      <div className="w-full bg-gray-800 p-6">
        {/* Hide heading in fullscreen mode */}
        {selectedVideo === null && (
          <h2 className="text-2xl font-bold text-white mb-6 text-center">📺 ISS Live Streams</h2>
        )}

        {livestreamsLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : issLivestreams.length === 0 ? (
          <div className="text-center text-gray-400">
            <p>No ISS livestreams currently available.</p>
            <p className="text-sm mt-2">Livestreams are updated every 6 hours from YouTube.</p>
          </div>
        ) : selectedVideo !== null ? (
          // Theatre view - video only, globe is handled above
          <div className="w-full">
            {theatreLayout === 'vertical' ? (
              // Vertical layout: Video below the existing globe
              <div className="max-w-6xl mx-auto">
                <div className="bg-gray-700 rounded-lg overflow-hidden">
                  <div className="aspect-video">
                    <iframe
                      src={issLivestreams[selectedVideo].url}
                      title={issLivestreams[selectedVideo].name}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-semibold text-white">{issLivestreams[selectedVideo].name}</h3>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setTheatreLayout('horizontal')}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors"
                        >
                          ↔️ Side-by-Side
                        </button>
                        <button
                          onClick={() => setSelectedVideo(null)}
                          className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                          ← Back to All Videos
                        </button>
                      </div>
                    </div>
                    <p className="text-gray-300">{issLivestreams[selectedVideo].description}</p>
                  </div>
                </div>
              </div>
            ) : (
              // Horizontal layout: Globe left, video right - responsive sizing
              <div className="w-full h-screen flex flex-col">
                <div className="flex-1 flex gap-6 p-6">
                  {/* Globe Section */}
                  <div className="flex-1 relative bg-gray-700 rounded-lg overflow-hidden">
                    <CesiumViewer
                      ref={cesiumViewerRef}
                      onViewerReady={() => setViewerReady(true)}
                      onViewerError={(error) => setError(error)}
                      onISSData={(data) => setIssData(data)}
                      onLoadingComplete={() => setLoading(false)}
                      onError={(error) => setError(error)}
                      onDataSourceChange={(usingDirect) => setUsingDirectPosition(usingDirect)}
                    />

                    {/* Control Panel in Horizontal Layout */}
                    <div className="absolute top-4 left-4 bg-black bg-opacity-75 text-white p-3 rounded-lg w-56">
                      <h2 className="text-lg font-bold mb-2">🛰️ ISS Tracker</h2>

                      {loading ? (
                        <div className="space-y-1">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                          <p className="text-xs text-gray-300">Loading...</p>
                        </div>
                      ) : error ? (
                        <div className="space-y-1">
                          <p className="text-xs text-red-400">⚠️ {error}</p>
                        </div>
                      ) : issData ? (
                        <div className="space-y-1">
                          <div className="text-xs">
                            <p><strong>Lat:</strong> {issData.latitude.toFixed(2)}°</p>
                            <p><strong>Lon:</strong> {issData.longitude.toFixed(2)}°</p>
                            <p><strong>Alt:</strong> {(issData.altitude / 1000).toFixed(1)} km</p>
                          </div>
                        </div>
                      ) : null}

                      {/* Zoom Slider */}
                      <div className="mt-3 space-y-2">
                        <label className="text-xs font-medium text-gray-300">Zoom</label>
                        <input
                          type="range"
                          min="100000"
                          max="10000000"
                          step="100000"
                          defaultValue="4000000"
                          onChange={(e) => handleZoomChange(parseInt(e.target.value))}
                          className="w-full h-1.5 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                        />
                      </div>
                    </div>

                    {/* Loading Overlay */}
                    {loading && (
                      <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                        <div className="bg-black bg-opacity-90 text-white p-4 rounded-lg text-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                          <h3 className="text-sm font-semibold mb-1">Loading...</h3>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Video Section */}
                  <div className="flex-1 bg-gray-700 rounded-lg overflow-hidden flex flex-col">
                    <div className="flex-1 flex items-center justify-center">
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="w-full h-full aspect-video">
                          <iframe
                            src={issLivestreams[selectedVideo].url}
                            title={issLivestreams[selectedVideo].name}
                            className="w-full h-full rounded"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          ></iframe>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 border-t border-gray-600 flex-shrink-0">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-semibold text-white">{issLivestreams[selectedVideo].name}</h3>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setTheatreLayout('vertical')}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-sm font-medium transition-colors"
                          >
                            ↕️ Stacked
                          </button>
                          <button
                            onClick={() => setSelectedVideo(null)}
                            className="bg-gray-600 hover:bg-gray-500 text-white px-3 py-1.5 rounded text-sm font-medium transition-colors"
                          >
                            ← Back
                          </button>
                        </div>
                      </div>
                      <p className="text-gray-300 text-sm">{issLivestreams[selectedVideo].description}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          // Grid view
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {issLivestreams.map((stream, index) => (
              <div key={index} className="bg-gray-700 rounded-lg overflow-hidden flex flex-col h-full">
                <div className="aspect-video">
                  <iframe
                    src={stream.url}
                    title={stream.name}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
                <div className="p-4 flex flex-col flex-grow">
                  <h3 className="text-lg font-semibold text-white mb-2">{stream.name}</h3>
                  <p className="text-sm text-gray-300 mb-3 flex-grow">{stream.description}</p>
                  <button
                    onClick={() => {
                      setSelectedVideo(index);
                      setTheatreLayout('vertical');
                    }}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors mt-auto"
                  >
                    🎭 Watch in Theatre View
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 text-center space-y-2">
          <p className="text-gray-400 text-sm">
            Watch live footage from the International Space Station and see what the astronauts are seeing in real-time.
            Match the views with the ISS position on the globe above.
          </p>
          <p className="text-amber-400 text-xs">
            ⚠️ Note: Livestream video may be slightly delayed compared to the ISS position map due to streaming latency of footage from space.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ISSVisualizer;
